import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { 
  LayoutDashboard, FileText, Users, CreditCard, 
  Settings, LogOut, Box, Activity, Briefcase, 
  Bell, ChevronLeft, ChevronRight, Database,
  ShieldCheck,User
} from "lucide-react";

import EvalLogo from "../Images/EvalLogo.png";

const Sidebar = ({ isOrg, onSignOut }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <aside 
      className={`hidden lg:flex flex-col bg-white border-r border-slate-300 sticky top-0 h-screen z-50 ${
        isCollapsed ? "w-20" : "w-72"
      }`} 
    >
      {/* 1. COLLAPSE TOGGLE */}
      <button 
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-8 w-6 h-6 bg-white border border-slate-300 flex items-center justify-center text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white z-50 shadow-sm"
      >
        {isCollapsed ? <ChevronRight size={12} strokeWidth={3}/> : <ChevronLeft size={12} strokeWidth={3}/>}
      </button>

      {/* 2. BRANDING */}
      <div className={`p-6 border-b border-slate-100 flex items-center gap-4 ${isCollapsed ? "justify-center" : ""}`}>
        <Link to="/dashboard" className="flex items-center select-none shrink-0 group">
          <div className="w-16 h-16 flex items-center justify-center overflow-hidden">
            <img 
              src={EvalLogo} 
              className="w-full h-full object-contain p-0 block pointer-events-none" 
              style={{ imageRendering: 'auto', transform: 'translateZ(0)' }}
              alt="EvalNDT" 
            />
          </div>
        </Link>
        
        {!isCollapsed && (
          <div className="flex flex-col overflow-hidden">
            <h1 className="text-xl font-black tracking-tighter text-slate-900 leading-none uppercase">
              EVAL<span className="text-[#1E3A8A]">NDT</span>
            </h1>
            <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.25em] mt-1">
              Focus NDT Group
            </span>
          </div>
        )}
      </div>

      {/* 3. MAIN NAVIGATION */}
      <nav className="flex-1 px-3 py-6 space-y-8 overflow-y-auto custom-scrollbar overflow-x-hidden">
        
        {/* ANALYTICS */}
        <div className="space-y-1">
          {!isCollapsed && <p className="px-4 mb-2 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Operational Analytics</p>}
          {/* Note: added 'end' prop to dashboard so it doesn't highlight when on /dashboard/certificate */}
          <SidebarItem to="/dashboard" icon={<LayoutDashboard />} label="System Overview" isCollapsed={isCollapsed} end={true} />
          <SidebarItem to="/live-feed" icon={<Activity />} label="Sensor Network" isCollapsed={isCollapsed} />
          <SidebarItem to="/reports" icon={<Database />} label="Report Registry" isCollapsed={isCollapsed} />
        </div>

        {/* OPERATIONS MODULE */}
        <div className="space-y-1">
          {!isCollapsed && (
            <p className="px-4 mb-2 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
              Asset & Compliance
            </p>
          )}
          <SidebarItem to="/inventory" icon={<Box />} label="Equipment Library" isCollapsed={isCollapsed} />
          <SidebarItem to="/projects" icon={<Briefcase />} label="Active Projects" isCollapsed={isCollapsed} />
          
          <SidebarItem 
            to="/dashboard/certificate" 
            icon={<ShieldCheck />} 
            label="Certifications" 
            isCollapsed={isCollapsed} 
            badge="EXP" 
          />

          {isOrg && <SidebarItem to="/team" icon={<Users />} label="Personnel" isCollapsed={isCollapsed} />}
        </div>

        {/* SYSTEM CONFIG */}
      {/* SYSTEM CONFIG / INFRASTRUCTURE */}
<div className="space-y-1">
  {!isCollapsed && (
    <p className="px-4 mb-2 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
      Infrastructure
    </p>
  )}
  
  {/* NEW: ACCOUNT REGISTRY */}
  <SidebarItem 
    to="/dashboard/account" 
    icon={<User />} 
    label="Account Registry" 
    isCollapsed={isCollapsed} 
  />

  <SidebarItem 
    to="/dashboard/billing" 
    icon={<CreditCard />} 
    label="System Billing / Credits" 
    isCollapsed={isCollapsed} 
  />

  <SidebarItem 
    to="/notifications" 
    icon={<Bell />} 
    label="Event Logs" 
    badge="03" 
    isCollapsed={isCollapsed} 
  />

  <SidebarItem 
    to="/settings" 
    icon={<Settings />} 
    label="Configuration" 
    isCollapsed={isCollapsed} 
  />
</div>
      </nav>

      {/* 4. FOOTER */}
      <div className="p-3 border-t border-slate-200 bg-slate-50">
        <button 
          onClick={onSignOut}
          className={`flex items-center gap-4 text-slate-500 hover:text-white hover:bg-rose-600 font-black text-[10px] uppercase tracking-widest w-full px-4 py-3 border border-transparent hover:border-rose-700 ${isCollapsed ? "justify-center px-0" : ""}`}
        >
          <LogOut size={16} />
          {!isCollapsed && <span>End Session</span>}
        </button>
      </div>
    </aside>
  );
};

const SidebarItem = ({ icon, label, to, badge, isCollapsed, end }) => (
  <NavLink 
    to={to}
    end={end} // Important for differentiating /dashboard from /dashboard/child-routes
    className={({ isActive }) => `
      group w-full flex items-center px-4 py-2.5 border transition-all duration-200
      ${isCollapsed ? "justify-center" : "justify-between"}
      ${isActive 
        ? 'bg-[#1E3A8A] border-[#1E3A8A] text-white shadow-[4px_4px_0px_0px_rgba(30,58,138,0.1)]' 
        : 'border-transparent text-slate-500 hover:bg-blue-50/50 hover:border-blue-100 hover:text-[#1E3A8A]'}
    `}
  >
    <div className="flex items-center gap-4">
      {React.cloneElement(icon, { 
        size: 16, 
        strokeWidth: 2.5,
        className: "shrink-0" 
      })} 
      {!isCollapsed && <span className="text-[11px] font-black uppercase tracking-wider whitespace-nowrap">{label}</span>}
    </div>
    
    {!isCollapsed && badge && (
      <span className={`text-[9px] font-black px-1.5 py-0.5 tabular-nums bg-amber-500 text-white`}>
        {badge}
      </span>
    )}
  </NavLink>
);

export default Sidebar;